import { Controller, FormProvider, useForm } from 'react-hook-form';
import Link from 'next/link';
import { useRouter } from 'next/router';

import dayjs from 'dayjs';

import useAcl from '@/hooks/user/useAcl';
import type { FormattedReportType } from '@/utils/hooks/reports/useEditReport';
import useFetchReports from '@/utils/hooks/swr/reports/useFetchReports';
import type { HashtagType } from '@/utils/hooks/swr/useHashtags';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { ErrorCustom } from '@/utils/client';
import { post } from '@/utils/client';
import { checkValidDashboardCustomization } from '@/utils/pdf/formatData/checkValidDashboardCustomization';
import useToggle from '@/utils/hooks/utils/useToggle';

import type { EmailType } from '@/types/brandedTypes/email';
import type { DashboardType } from '@/types/dashboardType';
import type { SocialProfileType } from '@/types/socialProfileType';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import reportFormTypesConfig, { REPORT_TYPES } from '@/lib/reports/reportFormTypesConfig';
import { CREATE_REPORT, EDIT_REPORT_SCHEDULED } from '@/lib/routes/apiEndpoints';
import { REPORTS, SUBSCRIPTION_INVOICES } from '@/lib/routes/privateRoutes';
import { INSTAGRAM } from '@/lib/socialProfile';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import { Select } from '@/components/base/Select/SelectComponent';
import DatepickerReports from '@/components/composite/Datepicker/DatepickerReports';
import { InputEmails } from '@/components/composite/Form/Input/InputEmails';
import { RadioGroup } from '@/components/composite/Form/RadioGroup';
import ContentSearchBox from '@/components/pages/reports/search/ContentSearchBox';
import { Trans } from '@/components/tools/translation/Trans';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
import ReportSuccessModal from '@/components/pages/reports/components/Modal/ReportSuccessModal';
import ReportContentPreview from '@/components/pages/reports/components/ReportContentPreview';
import ReportSectionTitle from '@/components/pages/reports/components/ReportSectionTitle';
import type {
    FrequencyType,
    ReportFormProps
} from '@/components/pages/reports/CreateEditPage/components/ReportForm';
import ReportFormBodyControlledBooleanRadioGroup from '@/components/pages/reports/CreateEditPage/components/ReportForm/ReportFormBodyControlledBooleanRadioGroup';

type ReportFormBodyBooleanFieldType = 0 | 1;

type ReportFormBodyContentType = SocialProfileType[] | HashtagType[] | DashboardType[];

export type ReportFormBodyDataType = {
    report_title: string;
    report_type: string;
    brand: ReportFormBodyBooleanFieldType;
    send_email: ReportFormBodyBooleanFieldType;
    emails: EmailType[];
    is_scheduled: ReportFormBodyBooleanFieldType;
    period: {
        period: FormattedReportType['period'];
        date?: string[];
    };
    frequency: FrequencyType;
    content: ReportFormBodyContentType;
};

type ReportFormCreateRequestParamsType = {
    report_title: string;
    report_type: string;
    brand: boolean;
    send_email: boolean;
    is_scheduled: boolean;
    period: FormattedReportType['period'];
    timezone: string;
    emails?: EmailType[];
    start_date?: string;
    end_date?: string;
    frequency?: FrequencyType;
    social_profiles?: SocialProfileType['id'][];
    dashboards?: DashboardType['id'][];
    hashtags?: HashtagType['id'][];
};

type ReportFormRequestErrorFieldsType =
    | keyof Omit<ReportFormBodyDataType, 'content'>
    | 'social_profiles'
    | 'hashtags'
    | 'dashboards';

export type ReportFormBodyProps = PickUnion<
    ReportFormProps,
    'type' | 'contentList' | 'initialData'
> & {
    setGlobalError?: (globalError: boolean) => void;
};

const ReportFormBody = ({ setGlobalError, initialData, ...restProps }: ReportFormBodyProps) => {
    const { t } = useTranslation();
    const { hasAcl } = useAcl();
    const isEdit = !!initialData;
    const router = useRouter();
    const { isOpen: isOpenSuccess, toggle: toggleSuccess } = useToggle();
    const { user } = useUser();
    const hasPartialAccess = !hasAcl(aclFeatures.report.create.full);
    const { mutateReports } = useFetchReports();
    const { mutateReports: mutateScheduledReports } = useFetchReports({
        isScheduled: true
    });
    const reportFormTypeConfig = reportFormTypesConfig.find(
        (config) => config.type === restProps.type
    );
    const frequencyOptions = [
        { value: 'every/day', label: t('reports:general.every_day') },
        { value: 'every/week', label: t('reports:general.every_week') },
        { value: 'every/month', label: t('reports:general.every_month') },
        { value: 'every/quarter', label: t('reports:general.every_quarter') },
        { value: 'every/year', label: t('reports:general.every_year') }
    ] as const;

    const defaultValues: ReportFormBodyDataType = {
        report_title: t(`reports:create.${restProps.type}_report_form_title`),
        report_type: `${restProps.type}_xls`,
        brand: 0,
        send_email: 0,
        emails: [user?.email],
        is_scheduled: 0,
        period: { period: reportFormTypeConfig.defaultPeriod },
        frequency: frequencyOptions[2].value,
        content: []
    };

    const formatInitialData = (): ReportFormBodyDataType => ({
        report_title: initialData?.name,
        report_type: `${initialData?.type}_${initialData?.format}`,
        brand: Number(initialData?.params?.brand) as ReportFormBodyBooleanFieldType,
        send_email: Number(initialData?.emails?.length > 0) as ReportFormBodyBooleanFieldType,
        emails: initialData?.emails,
        is_scheduled: 1,
        period: { period: initialData?.period },
        frequency: initialData?.frequency,
        content: []
    });

    const formMethods = useForm<ReportFormBodyDataType>({
        defaultValues: isEdit ? formatInitialData() : defaultValues
    });

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        setError,
        formState: { errors, isSubmitting, isValid, isDirty }
    } = formMethods;

    const createReport = async (data: ReportFormBodyDataType) => {
        const convertedData: ReportFormCreateRequestParamsType = {
            report_title: data.report_title,
            report_type: data.report_type,
            brand: Boolean(data.brand),
            send_email: Boolean(data.send_email),
            is_scheduled: Boolean(data.is_scheduled),
            period: data.period.period,
            timezone: user?.settings?.timezone
        };
        // Set the content field parameters
        convertedData[reportFormTypeConfig.contentField.name] = data.content.map((item) => item.id);
        // Add emails field if necessary
        if (convertedData.send_email) {
            convertedData['emails'] = data.emails;
        }
        // Add dates if necessary
        if (convertedData.period === 'custom') {
            convertedData['start_date'] = dayjs(data.period.date[0]).format('YYYY-MM-DD HH:mm:ss');
            convertedData['end_date'] = dayjs(data.period.date[1]).format('YYYY-MM-DD HH:mm:ss');
        }
        // Set frequency
        if (convertedData.is_scheduled) {
            convertedData['frequency'] = data.frequency;
        }
        // Override brand field if necessary
        if (convertedData.brand && convertedData.report_type !== `${restProps.type}_pdf`) {
            convertedData['brand'] = false;
        }

        await post(CREATE_REPORT, convertedData);
    };

    const editReport = async (data: ReportFormBodyDataType) => {
        await post(EDIT_REPORT_SCHEDULED, {
            scheduler_id: initialData?.id,
            frequency: data?.frequency,
            period: data?.period?.period,
            emails: data?.emails
        });
    };

    const handleSubmitError = (e: ErrorCustom) => {
        const errorsFields = e.response?.data?.errors;
        if (e.status === 422 && errorsFields) {
            Object.keys(errorsFields).forEach((field: ReportFormRequestErrorFieldsType) => {
                const fieldName =
                    field === reportFormTypeConfig.contentField.name ? 'content' : field;
                setError(fieldName as keyof ReportFormBodyDataType, {
                    type: 'manual',
                    message: t(
                        `errors:error_reports.${
                            errorsFields[field][0] === 'required'
                                ? 'field_required'
                                : 'field_not_valid'
                        }`
                    )
                });
            });
        } else {
            setGlobalError(true);
        }
    };

    const onSubmit = async (data: ReportFormBodyDataType) => {
        try {
            setGlobalError(false);
            if (isEdit) {
                await editReport(data);
            } else {
                await createReport(data);
            }
            if (data.is_scheduled) {
                // Refetch scheduled reports
                await mutateScheduledReports();
            } else {
                // Refetch not scheduled reports
                await mutateReports();
            }
            toggleSuccess();
        } catch (e) {
            handleSubmitError(e);
        }
    };

    const getSelectedCustomDashboardsNb = () => {
        const content = watch('content');
        if (
            restProps.type === REPORT_TYPES.DASHBOARD &&
            hasAcl(aclFeatures.report.white_label.read) &&
            content?.length !== 0
        ) {
            return content?.filter((dashboard: DashboardType) =>
                checkValidDashboardCustomization(dashboard?.data?.report_customization)
            )?.length;
        }

        return 0;
    };

    const shouldDisplayCustomDashboardMessage = () => {
        const selectedCustomDashboardsNb = getSelectedCustomDashboardsNb();
        return (
            selectedCustomDashboardsNb > 0 &&
            selectedCustomDashboardsNb !== watch('content')?.length
        );
    };

    const disabledWhenOnlyCustomDashboards = () => {
        const selectedCustomDashboardsNb = getSelectedCustomDashboardsNb();
        return (
            selectedCustomDashboardsNb > 0 &&
            selectedCustomDashboardsNb === watch('content')?.length
        );
    };

    const getDatepickerReportTypeProps = (content: ReportFormBodyDataType['content']) => {
        switch (restProps.type) {
            case REPORT_TYPES.ACCOUNT:
                return { selectedOptions: content as SocialProfileType[] };
            case REPORT_TYPES.DASHBOARD:
                return {
                    selectedOptions: (content as DashboardType[])?.flatMap(
                        (dashboard) => dashboard.data.social_profiles
                    )
                };
            case REPORT_TYPES.COMPETITOR:
                return {
                    selectedOptions: content as SocialProfileType[],
                    newMinDate: dayjs()
                        .utc()
                        .tz(user?.settings.timezone)
                        .subtract(3, 'month')
                        .startOf('day')
                        .format()
                };
            case REPORT_TYPES.HASHTAG:
                return {
                    selectedOptions: [{ social_network_type: INSTAGRAM }],
                    newMinDate: dayjs()
                        .utc()
                        .tz(user?.settings.timezone)
                        .subtract(29, 'days')
                        .startOf('day')
                        .format()
                };
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex w-full flex-col-reverse lg:grid lg:grid-cols-2 lg:gap-8">
                    <div className="full">
                        <ReportSectionTitle title={t('reports:create.report_infos')} />
                        <div className="mb-7 w-full">
                            <Label required htmlFor="report_title">
                                {t('reports:create.label_title')}
                            </Label>
                            <Input
                                disabled={
                                    isSubmitting || disabledWhenOnlyCustomDashboards() || isEdit
                                }
                                id="report_title"
                                autoComplete="off"
                                placeholder={t('reports:create.label_title')}
                                name="report_title"
                                error={errors?.report_title?.message}
                                {...register('report_title', {
                                    required: t('errors:error_reports.field_required')
                                })}
                            />
                            {shouldDisplayCustomDashboardMessage() ? (
                                <span className="font-GMedium text-xs italic text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    {t('reports:create.notice_only_apply_on_non_custom')}
                                </span>
                            ) : null}
                        </div>
                        <Controller
                            control={control}
                            name={'report_type'}
                            rules={{
                                required: true
                            }}
                            render={({ field: { onChange, value } }) => (
                                <RadioGroup
                                    disabled={
                                        isSubmitting ||
                                        reportFormTypeConfig?.formats?.length <= 1 ||
                                        isEdit ||
                                        hasPartialAccess
                                    }
                                    isLocked={hasPartialAccess}
                                    label={t('reports:create.label_format')}
                                    values={reportFormTypeConfig.formats.map((format) => ({
                                        id: `report_type_${format}`,
                                        value: `${restProps.type}_${format}`,
                                        label: format.toUpperCase(),
                                        name: `report_type`
                                    }))}
                                    onChange={onChange}
                                    current={value}
                                />
                            )}
                        />
                        {hasAcl(aclFeatures.report.white_label.read) &&
                        watch('report_type') === `${restProps.type}_pdf` ? (
                            <div className="mb-8 flex flex-col space-y-1">
                                <ReportFormBodyControlledBooleanRadioGroup
                                    name="brand"
                                    label={t('reports:create.label_brand')}
                                    tooltip={t('reports:create.brand_tooltip')}
                                    disabled={
                                        isSubmitting || disabledWhenOnlyCustomDashboards() || isEdit
                                    }
                                    className="w-full"
                                />
                                {shouldDisplayCustomDashboardMessage() ? (
                                    <span className="font-GMedium text-xs italic text-icoGray-500 dark:text-icoDarkMode-wolf">
                                        {t('reports:create.notice_only_apply_on_non_custom')}
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                        <ReportSectionTitle title={t('reports:create.report_settings')} />
                        <ReportFormBodyControlledBooleanRadioGroup
                            name="send_email"
                            label={t('reports:create.label_send_email')}
                            disabled={isSubmitting || isEdit || hasPartialAccess}
                            isLocked={hasPartialAccess}
                        />
                        {watch('send_email') ? (
                            <Controller
                                control={control}
                                name={'emails'}
                                rules={{
                                    required: t('errors:error_reports.email_min_length')
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <InputEmails
                                        required={true}
                                        emails={value}
                                        onSetEmails={(email) => {
                                            onChange([...value, email]);
                                        }}
                                        onRemoveEmail={onChange}
                                        errors={errors?.emails?.message}
                                    />
                                )}
                            />
                        ) : null}
                        {watch('period')?.period !== 'custom' ? (
                            <ReportFormBodyControlledBooleanRadioGroup
                                name="is_scheduled"
                                label={t('reports:create.label_schedule_report')}
                                disabled={isSubmitting || isEdit || hasPartialAccess}
                                isLocked={hasPartialAccess}
                            />
                        ) : null}
                        <div className="my-2 flex flex-row items-center gap-4">
                            <Label className="!mb-0 flex max-w-max cursor-pointer items-center">
                                {t('reports:create.label_period')}
                            </Label>
                            {hasPartialAccess ? (
                                <TooltipPlanRestriction position="top">
                                    <Icon icon="crown" className="text-icoBlue" />
                                </TooltipPlanRestriction>
                            ) : null}
                        </div>
                        <div className="mb-8 w-full">
                            <Controller
                                control={control}
                                name={'period'}
                                rules={{
                                    validate: (el) =>
                                        el?.period !== undefined ||
                                        t('errors:error_reports.field_required')
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <>
                                        <DatepickerReports
                                            periods={`${
                                                reportFormTypeConfig.allowedDatepickerPeriods
                                            }${isEdit ? '_edit' : ''}`}
                                            periodSelected={
                                                hasPartialAccess
                                                    ? { period: 'last_30_days' }
                                                    : value
                                            }
                                            isDisabled={hasPartialAccess}
                                            setPeriodSelected={(data) => {
                                                onChange(
                                                    data.periodSelected === 'custom'
                                                        ? {
                                                              period: data.periodSelected,
                                                              date: data.date
                                                          }
                                                        : { period: data.periodSelected }
                                                );
                                                if (data.periodSelected === 'custom') {
                                                    setValue('is_scheduled', 0);
                                                }
                                            }}
                                            {...getDatepickerReportTypeProps(watch('content'))}
                                        />
                                        {hasPartialAccess ? (
                                            <span className="mt-2 font-GMedium text-xs italic text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                <Trans
                                                    i18nKey="reports:create.max_period_tooltip"
                                                    components={{
                                                        link: (
                                                            <Link
                                                                className="text-icoBlue"
                                                                href={SUBSCRIPTION_INVOICES}
                                                            />
                                                        )
                                                    }}
                                                />
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            />
                        </div>
                        {watch('is_scheduled') && watch('period')?.period !== 'custom' ? (
                            <Controller
                                control={control}
                                name={'frequency'}
                                rules={{
                                    validate: (el) =>
                                        el !== undefined || t('errors:error_reports.field_required')
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        disabled={isSubmitting}
                                        selected={
                                            frequencyOptions?.find(
                                                (item) => item.value === value
                                            ) ?? frequencyOptions[2]
                                        }
                                        error={errors?.frequency?.message}
                                        setSelected={(option) => onChange(option.value)}
                                        label={t('reports:general.frequency')}
                                        options={frequencyOptions}
                                    />
                                )}
                            />
                        ) : null}
                    </div>
                    <div className="mb-8 w-full">
                        <ReportSectionTitle title={t('reports:create.report_content')} />
                        {isEdit ? (
                            <>
                                <Label required>{t(reportFormTypeConfig.label)}</Label>
                                <ReportContentPreview value={initialData} size="base" />
                            </>
                        ) : (
                            <>
                                <Label required>{t(reportFormTypeConfig.contentField.label)}</Label>
                                <Controller
                                    control={control}
                                    name={'content'}
                                    rules={{
                                        validate: (el) =>
                                            el?.length >= 1 ||
                                            t('errors:error_reports.field_required')
                                    }}
                                    render={({ field: { onChange } }) => (
                                        <ContentSearchBox
                                            onSetList={onChange}
                                            errors={errors?.content?.message}
                                            isLoading={false}
                                            disabled={false}
                                            {...restProps}
                                        />
                                    )}
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className="flex w-full justify-end">
                    <Button
                        type="submit"
                        id="download-report-submit"
                        size="lg"
                        isLoading={isSubmitting}
                        className="mt-3 w-auto"
                        disabled={isSubmitting || !isValid || (isEdit && !isDirty)}>
                        {t(isEdit ? 'reports:edit.edit_report' : 'reports:create.download_report')}
                    </Button>
                </div>
                <ReportSuccessModal
                    isOpen={isOpenSuccess}
                    toggle={() => router.push(REPORTS)}
                    isScheduled={Boolean(watch('is_scheduled'))}
                    isEdit={isEdit}
                />
            </form>
        </FormProvider>
    );
};

export default ReportFormBody;
